import React, { useMemo } from 'react';
import { Pill, Search, X } from 'lucide-react';
import Select from '../../Select';
import ReportTable from '../shared/ReportTable';
import { fmtMoney, fmtNum } from '../shared/chartTheme';
import {
  CATEGORY_LABEL, DASH, STOCK_STATUS_LABEL, StatusBadge, fmtDate, human,
} from './medicineFormat';

// The first half of the Medicine Profile: the whole catalogue, medicines and supplies, filtered as
// you type. It is a few hundred rows, so it arrives once and every filter works on the screen.
// Same-name items are common (a syrup and a tablet of one brand), so each row shows its code,
// form and strength, and opening one always goes by its id.

const STATUS_FILTERS = ['IN_STOCK', 'LOW_STOCK', 'NEAR_EXPIRY', 'OUT_OF_STOCK', 'EXPIRED'];

export const EMPTY_FILTERS = { query: '', category: 'ALL', status: 'ALL', type: '', hideDiscontinued: false };

/** Every word typed must appear somewhere in the item's name, generic name, code, maker or strength. */
export const matchesQuery = (row, query) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [row.name, row.genericName, row.code, row.manufacturer, row.strength, human(row.type)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return words.every((w) => hay.includes(w));
};

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
      active ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    {children}
  </button>
);

const buildColumns = (usageDays) => [
  {
    key: 'name',
    label: 'Item',
    wrap: true,
    render: (r) => (
      <div>
        <div className="font-medium text-gray-900">
          {r.name}
          {/* A real space, not a margin: it is what copy-paste and screen readers see. */}
          {r.strength && <span className="font-normal text-gray-600">{` ${r.strength}`}</span>}
        </div>
        {r.genericName && <div className="text-xs text-gray-500">{r.genericName}</div>}
      </div>
    ),
    csv: (r) => [r.name, r.strength].filter(Boolean).join(' '),
    searchText: (r) => [r.name, r.strength, r.genericName].filter(Boolean).join(' '),
  },
  { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs">{r.code}</span> },
  { key: 'type', label: 'Form', render: (r) => human(r.type), csv: (r) => human(r.type) },
  { key: 'category', label: 'Kind', render: (r) => CATEGORY_LABEL[r.category] || human(r.category), csv: (r) => CATEGORY_LABEL[r.category] || r.category },
  {
    key: 'stock',
    label: 'Stock',
    align: 'right',
    render: (r) => (
      <span title={r.legacyCounter ? 'Hand-kept count: never received on a stock receipt' : undefined}>
        {fmtNum(r.stock)}
        {r.legacyCounter && <span className="ml-1 text-xs text-gray-400">(count)</span>}
      </span>
    ),
    csv: (r) => r.stock,
    sortValue: (r) => Number(r.stock),
  },
  {
    key: 'status',
    label: 'Status',
    render: (r) => (
      <span className="inline-flex flex-wrap gap-1">
        <StatusBadge value={r.status} label={STOCK_STATUS_LABEL[r.status]} />
        {Number(r.expiredUnits) > 0 && r.status !== 'EXPIRED' && (
          <StatusBadge label={`${fmtNum(r.expiredUnits)} expired`} tone="red" />
        )}
        {r.discontinued && <StatusBadge label="Discontinued" tone="grey" />}
      </span>
    ),
    csv: (r) => `${STOCK_STATUS_LABEL[r.status] || r.status}${r.discontinued ? ' (discontinued)' : ''}`,
  },
  { key: 'sellingPrice', label: 'Price', align: 'right', render: (r) => fmtMoney(r.sellingPrice), csv: (r) => r.sellingPrice, sortValue: (r) => Number(r.sellingPrice) },
  { key: 'nextExpiry', label: 'Next Expiry', render: (r) => fmtDate(r.nextExpiry), csv: (r) => r.nextExpiry || '' },
  { key: 'lastDispensed', label: 'Last Given', render: (r) => fmtDate(r.lastDispensed), csv: (r) => (r.lastDispensed || '').slice(0, 10) },
  {
    key: 'dispensedRecent',
    label: `Given, ${usageDays} Days`,
    align: 'right',
    render: (r) => (Number(r.dispensedRecent) ? fmtNum(r.dispensedRecent) : DASH),
    csv: (r) => r.dispensedRecent,
    sortValue: (r) => Number(r.dispensedRecent),
  },
];

const MedicineCataloguePanel = ({ data, loading, filters, setFilters, onOpen }) => {
  const rows = useMemo(() => data?.rows || [], [data]);
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  // Filtered in two steps so each chip can show how many it would leave, given the other filters.
  const base = useMemo(
    () =>
      rows.filter(
        (r) =>
          matchesQuery(r, filters.query) &&
          (!filters.type || r.type === filters.type) &&
          (!filters.hideDiscontinued || !r.discontinued)
      ),
    [rows, filters.query, filters.type, filters.hideDiscontinued]
  );
  const count = (pred) => base.filter(pred).length;
  const statusOk = (r) => filters.status === 'ALL' || r.status === filters.status;
  const shown = useMemo(
    () =>
      base.filter(
        (r) =>
          (filters.category === 'ALL' || r.category === filters.category) &&
          (filters.status === 'ALL' || r.status === filters.status)
      ),
    [base, filters.category, filters.status]
  );

  const typeOptions = useMemo(() => {
    const types = [...new Set(rows.map((r) => r.type).filter(Boolean))];
    return [
      { value: '', label: 'Any form' },
      ...types.map((t) => ({ value: t, label: human(t) })).sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [rows]);

  const anyFilter =
    filters.query || filters.type || filters.hideDiscontinued || filters.category !== 'ALL' || filters.status !== 'ALL';
  const usageDays = data?.usageWindowDays || 30;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Pill className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Find a medicine or supply</h3>
            <p className="text-sm text-gray-500">
              Type a name, generic name, code or maker, then open an item to see its stock history, batches, prices, and
              who it was given to.
            </p>
          </div>
        </div>

        <div className="relative">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            autoFocus
            value={filters.query}
            onChange={(e) => set('query')(e.target.value)}
            placeholder="e.g. panadol, amoxicillin, MED-TAB-0002"
            aria-label="Search medicines and supplies"
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          {filters.query && (
            <button
              type="button"
              onClick={() => set('query')('')}
              aria-label="Clear the search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Chip active={filters.category === 'ALL'} onClick={() => set('category')('ALL')}>All ({count(statusOk)})</Chip>
          <Chip active={filters.category === 'MEDICINE'} onClick={() => set('category')('MEDICINE')}>
            Medicines ({count((r) => r.category === 'MEDICINE' && statusOk(r))})
          </Chip>
          <Chip active={filters.category === 'SUPPLY'} onClick={() => set('category')('SUPPLY')}>
            Supplies ({count((r) => r.category === 'SUPPLY' && statusOk(r))})
          </Chip>
          <span className="w-px h-6 bg-gray-200 mx-1" />
          <Chip active={filters.status === 'ALL'} onClick={() => set('status')('ALL')}>Any status</Chip>
          {STATUS_FILTERS.map((s) => (
            <Chip key={s} active={filters.status === s} onClick={() => set('status')(s)}>
              {STOCK_STATUS_LABEL[s]} ({count((r) => r.status === s && (filters.category === 'ALL' || r.category === filters.category))})
            </Chip>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="w-56">
            <Select value={filters.type} onChange={set('type')} options={typeOptions} placeholder="Any form" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={filters.hideDiscontinued}
              onChange={(e) => set('hideDiscontinued')(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            Hide discontinued items
          </label>
          {anyFilter && (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="ml-auto text-sm font-medium text-emerald-700 hover:text-emerald-800"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <ReportTable
        title={loading && !data ? 'Loading the catalogue' : `${shown.length} item${shown.length === 1 ? '' : 's'}`}
        columns={buildColumns(usageDays)}
        rows={shown}
        searchable={false}
        onRowClick={(r) => onOpen(r.id)}
        csvName={`medicine-catalogue-${data?.asOf || ''}`}
        emptyText={loading ? 'Loading' : 'Nothing matches. Try part of the name, or clear the filters.'}
        footnote={
          rows.length
            ? `Showing ${shown.length} of ${rows.length} items${anyFilter ? ', filtered' : ''}. Click one to open its profile. Stock marked (count) is a hand-kept count, not batches.`
            : undefined
        }
      />
    </div>
  );
};

export default MedicineCataloguePanel;
